import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { ExternalLink, MessageSquare, Search, Send, Wrench } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ProposalControls, ChangeList } from './ProposalControls'
import { STATUS_PILL } from './ProposedActions'
import { stepSummary } from './stepSummary'
import { useCreateActionLinearIssue } from '@/queries/investigations'
import { buildEvidenceGraph, edgesOf, findingId, isEdgeLit, SOURCE_LABEL, sourceOf, upstream, type Edge, type SourceKind } from './evidence'
import type { InvestigationResult, InvestigationStep, LinearIssueRef, ProposedAction } from '@/types/investigation'

const SOURCE_TINT: Record<SourceKind, string> = {
  ticket: 'bg-etn-success-subtle text-etn-success-text',
  data: 'bg-etn-info-subtle text-etn-info-text',
  logs: 'bg-etn-warning-subtle text-etn-warning-text',
  code: 'bg-etn-accent-subtle text-etn-accent-text-active',
  agent: 'bg-etn-control-subtle text-etn-text-label'
}

const DRAFT_NODE = 'draft'

function SourceBadge({ kind }: { kind: SourceKind }) {
  return (
    <span
      aria-hidden
      className={`grid h-[22px] w-[22px] flex-none place-items-center rounded-md font-mono text-etn-micro font-bold ${SOURCE_TINT[kind]}`}
    >
      {SOURCE_LABEL[kind]}
    </span>
  )
}

export function stepTitle(step: InvestigationStep): string {
  return step.tool.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())
}

// An action no propose_* tool can carry out (a code fix, a test) is engineering's to do
function ActionLinearIssue({ ticketId, invId, index, issue }: { ticketId: string; invId: string; index: number; issue?: LinearIssueRef | null }) {
  const create = useCreateActionLinearIssue(ticketId, invId)
  if (issue?.url) {
    return (
      <Button size="xs" variant="outline" className="mt-2" asChild>
        <a href={issue.url} target="_blank" rel="noreferrer">
          {issue.identifier ?? 'Linear issue'}
          <ExternalLink />
        </a>
      </Button>
    )
  }
  return (
    <Button size="xs" variant="outline" className="mt-2" disabled={create.isPending} onClick={() => create.mutate(index)}>
      <Send />
      {create.isPending ? 'Creating…' : 'Create Linear issue'}
    </Button>
  )
}

function BasedOn({ labels }: { labels: string[] }) {
  if (!labels.length) return null
  // Lines carry this on wide screens; stacked, the chips do
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1 text-etn-micro text-etn-text-soft md:hidden" data-testid="based-on">
      Based on:
      {labels.map(label => (
        <span key={label} className="rounded-etn-pill bg-etn-control-subtle px-2 py-0.5">
          {label}
        </span>
      ))}
    </div>
  )
}

export interface EvidenceMapProps {
  ticketId: string
  invId: string
  result: InvestigationResult
  steps: InvestigationStep[]
  proposals: ProposedAction[]
  reporterName?: string
  onUseDraft?: () => void
}

/**
 * Layout B from the approved mockup: what the Support Agent read, what it found, what it proposes.
 * Hovering or focusing any node lights the chain it rests on; the same chain is drawn as lines on
 * wide screens and listed as "Based on" chips when the columns stack.
 */
export function EvidenceMap({ ticketId, invId, result, steps, proposals, reporterName, onUseDraft }: EvidenceMapProps) {
  const graph = useMemo(() => buildEvidenceGraph(result, steps, proposals), [result, steps, proposals])
  const links = useMemo(() => {
    const all = new Map(graph.links)
    all.set(
      DRAFT_NODE,
      graph.findings.map(f => f.node.id)
    )
    return all
  }, [graph])
  const edges = useMemo(() => edgesOf(links), [links])

  const [active, setActive] = useState<string | null>(null)
  const lit = useMemo(() => (active ? upstream(active, links) : new Set<string>()), [active, links])

  const container = useRef<HTMLDivElement>(null)
  const nodes = useRef(new Map<string, HTMLElement>())
  const [paths, setPaths] = useState<{ edge: Edge; d: string }[]>([])

  const draw = useCallback(() => {
    const box = container.current?.getBoundingClientRect()
    if (!box) return
    setPaths(
      edges.flatMap(edge => {
        const from = nodes.current.get(edge.from)?.getBoundingClientRect()
        const to = nodes.current.get(edge.to)?.getBoundingClientRect()
        if (!from || !to || !from.width || !to.width) return []
        const a = { x: from.right - box.left, y: from.top + from.height / 2 - box.top }
        const b = { x: to.left - box.left, y: to.top + to.height / 2 - box.top }
        const mx = (a.x + b.x) / 2
        return [{ edge, d: `M${a.x},${a.y} C${mx},${a.y} ${mx},${b.y} ${b.x},${b.y}` }]
      })
    )
  }, [edges])

  useLayoutEffect(() => {
    draw()
    if (typeof ResizeObserver === 'undefined' || !container.current) return undefined
    const observer = new ResizeObserver(() => draw())
    observer.observe(container.current)
    return () => observer.disconnect()
  }, [draw])

  const register = (id: string) => (el: HTMLElement | null) => {
    if (el) nodes.current.set(id, el)
    else nodes.current.delete(id)
  }

  // Focus does what hover does, so the chain can be traced from the keyboard. Focus moving between a
  // node's own buttons stays on that node.
  const nodeProps = (id: string) => ({
    ref: register(id),
    tabIndex: 0,
    'data-node': id,
    'data-lit': active ? String(lit.has(id)) : undefined,
    onMouseEnter: () => setActive(id),
    onMouseLeave: () => setActive(current => (current === id ? null : current)),
    onFocus: () => setActive(id),
    onBlur: (e: React.FocusEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setActive(current => (current === id ? null : current))
    },
    className: `relative z-[1] mb-2.5 flex gap-2.5 rounded-etn-card border p-3 text-left transition-[border-color,background-color,opacity] duration-150 focus-visible:outline-2 focus-visible:outline-etn-accent ${
      active
        ? lit.has(id)
          ? 'border-etn-accent bg-etn-accent-subtle'
          : 'border-etn-border-hairline bg-etn-surface opacity-40'
        : 'border-etn-border-hairline bg-etn-surface'
    }`
  })

  const readTitle = (index: number) => stepTitle(steps[index])
  const findingTitle = (id: string) => result.findings?.[Number(id.slice(1))]?.title ?? ''
  const sources = new Set(graph.reads.map(r => sourceOf(r.step.tool)))

  const column = (title: string, sub: string, children: ReactNode) => (
    <div className="min-w-0">
      <h3 className="mb-0.5 text-etn-control font-semibold">{title}</h3>
      <p className="mb-3 text-xs text-etn-text-soft">{sub}</p>
      {children}
    </div>
  )

  return (
    <div>
      <div
        ref={container}
        className="relative grid grid-cols-1 gap-7 md:grid-cols-3"
        data-testid="evidence-map"
        data-focusing={active ? 'true' : 'false'}
      >
        <svg aria-hidden className="pointer-events-none absolute inset-0 z-0 hidden h-full w-full overflow-visible md:block">
          {paths.map(({ edge, d }) => (
            <path
              key={`${edge.from}-${edge.to}`}
              d={d}
              fill="none"
              data-lit={active ? String(isEdgeLit(edge, lit)) : undefined}
              className={
                active
                  ? isEdgeLit(edge, lit)
                    ? 'stroke-etn-accent [stroke-width:2]'
                    : 'stroke-etn-border-hairline opacity-20'
                  : 'stroke-etn-border-row [stroke-width:1.5]'
              }
            />
          ))}
        </svg>

        {column(
          'What it read',
          `${graph.reads.length} ${graph.reads.length === 1 ? 'read' : 'reads'} across ${sources.size} ${sources.size === 1 ? 'source' : 'sources'}`,
          graph.reads.map(({ node, step, index }) => (
            <div key={node.id} {...nodeProps(node.id)}>
              <SourceBadge kind={sourceOf(step.tool)} />
              <div className="min-w-0">
                <div className="text-etn-control font-semibold">{stepTitle(step)}</div>
                <div className="break-words text-xs text-etn-text-soft" title={step.why || undefined}>
                  {stepSummary(step) || `Step ${index}`}
                </div>
              </div>
            </div>
          ))
        )}

        {column(
          'What it found',
          'Hover or focus a finding to trace it',
          graph.findings.length ? (
            graph.findings.map(({ node, index }) => {
              const finding = result.findings![index]
              return (
                <div key={node.id} {...nodeProps(node.id)}>
                  <SourceBadge kind="agent" />
                  <div className="min-w-0">
                    <div className="text-etn-control font-semibold">{finding.title}</div>
                    {finding.detail && <div className="break-words text-xs text-etn-text-soft">{finding.detail}</div>}
                    <BasedOn labels={node.links.map(id => readTitle(Number(id.slice(1))))} />
                  </div>
                </div>
              )
            })
          ) : (
            <p className="text-xs text-etn-text-soft">This run listed no separate findings. Its conclusion is above.</p>
          )
        )}

        {column(
          'What it proposes',
          'Nothing changes until someone applies it',
          <>
            {graph.proposals.map(({ node, proposal }) => (
              <div key={node.id} {...nodeProps(node.id)} data-testid={`map-proposal-${proposal._id}`}>
                <SourceBadge kind="data" />
                <div className={`min-w-0 flex-1 space-y-2 ${proposal.status === 'dismissed' ? 'opacity-60' : ''}`}>
                  <div className="flex items-start gap-2">
                    <div className="text-etn-control font-semibold">{proposal.summary}</div>
                    <span
                      className={`ml-auto shrink-0 rounded-etn-pill px-2 py-0.5 text-etn-micro font-semibold ${STATUS_PILL[proposal.status]?.className ?? ''}`}
                    >
                      {STATUS_PILL[proposal.status]?.label ?? proposal.status}
                    </span>
                  </div>
                  <ChangeList proposal={proposal} />
                  <BasedOn labels={node.links.map(findingTitle)} />
                  <ProposalControls ticketId={ticketId} invId={invId} proposal={proposal} />
                </div>
              </div>
            ))}
            {graph.actions.map(({ node, index }) => {
              const action = result.actions[index]
              const Icon = action.kind === 'resolve' ? Wrench : Search
              return (
                <div key={node.id} {...nodeProps(node.id)}>
                  <Icon aria-hidden className="mt-0.5 h-4 w-4 flex-none text-etn-text-soft" />
                  <div className="min-w-0">
                    <div className="text-etn-control font-semibold">{action.title}</div>
                    <div className="break-words text-xs text-etn-text-soft">
                      {action.kind === 'resolve' ? 'Fix' : 'Follow-up'}
                      {action.detail ? `: ${action.detail}` : ''}
                    </div>
                    <ActionLinearIssue ticketId={ticketId} invId={invId} index={index} issue={action.linearIssue} />
                    <BasedOn labels={node.links.map(findingTitle)} />
                  </div>
                </div>
              )
            })}
            {result.draftReply && (
              <div {...nodeProps(DRAFT_NODE)}>
                <MessageSquare aria-hidden className="mt-0.5 h-4 w-4 flex-none text-etn-text-soft" />
                <div className="min-w-0 flex-1">
                  <div className="text-etn-control font-semibold">Reply to {reporterName || 'the reporter'}</div>
                  <div className="text-xs text-etn-text-soft">A draft that explains the cause and the fix</div>
                  {onUseDraft && (
                    <Button size="xs" variant="outline" className="mt-2" onClick={onUseDraft}>
                      Use draft reply
                    </Button>
                  )}
                  <BasedOn labels={graph.findings.map(f => findingTitle(findingId(f.index)))} />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {graph.skipped.length > 0 && (
        <div
          className="mt-4 space-y-1 rounded-etn-card bg-etn-surface-sunken px-3.5 py-3 text-etn-control text-etn-text-label"
          data-testid="skipped-steps"
        >
          {graph.skipped.map(({ step, index }) => (
            <p key={index}>
              <span className="mr-2 rounded-etn-pill bg-etn-danger-subtle px-2 py-0.5 text-etn-micro font-semibold text-etn-danger-text">
                Skipped
              </span>
              {stepTitle(step)}: {stepSummary(step)}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
